/**
 * Turn a base64 payload from an admin API into a browser download.
 *
 * Several admin exports return their file inline as base64 rather than via an
 * S3 round-trip (the row counts are small) — the org runs+cost CSV, and the
 * referral-partner commission statement (Trello #362). Extracted when the
 * second consumer arrived so the decode/Blob/anchor/revoke dance lives in one
 * place, per `general/abstract-before-duplicating.md`.
 */

/**
 * Decode a base64 string to bytes. Exported for direct unit testing.
 *
 * Backed by an explicit `ArrayBuffer` so the result types as
 * `Uint8Array<ArrayBuffer>` — the plain `new Uint8Array(n)` form widens to
 * `ArrayBufferLike` under TS 5.7+, which `BlobPart` rejects.
 */
export function base64ToBytes(base64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * How long to leave the object URL alive after the click. Revoking it
 * SYNCHRONOUSLY races the browser's fetch of the blob and can abort the
 * download — so the revoke is deferred to a later task instead. The anchor
 * itself is removed immediately; the click has already been dispatched.
 */
const REVOKE_DELAY_MS = 1000;

/**
 * Trigger a browser download of `base64` as `filename`.
 *
 * The anchor is removed and the object URL revoked in a `finally`, so a click
 * that throws can't leave a stray `<a>` in the DOM or leak the blob for the
 * life of the page.
 */
export function downloadBase64File(base64: string, filename: string, mimeType: string): void {
  const blob = new Blob([base64ToBytes(base64)], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  try {
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
  }
}
